import * as React from 'react';
import { useState } from 'react';
import {
    useListContext,
    useNotify,
    RaRecord,
} from 'react-admin';
import { Button } from '@mui/material';
import { CheckCircleOutline, BlockOutlined } from '@mui/icons-material';
import { ConfirmActionDialog } from './ConfirmActionDialog';

/**
 * Props for the VerificationActions component
 */
interface ReviewQueueActionsProps {
    /** Whether to display the component */
    display: boolean;
    /** The data provider for API calls */
    dataProvider: any;
    /** The resource name for API calls (e.g., 'RTP_Categories', 'RTP_Performers') */
    resource?: string;
    /** Action type for API calls */
    action?: string;
    /** The entity type for display text (e.g., 'Categories', 'Performers') */
    entityType: string;
    /** Market ID for API calls */
    marketId: number;
    /** Function to update the component state
     *  Use to open backdrop in parent component after successful API call
    */
    setState: React.Dispatch<React.SetStateAction<any>>;
    /** Function to reload the page */
    reloadPage: () => void;
}

/**
 * Shared Verification Actions Component
 * Provides bulk action buttons for updating verification status to Whitelist or Blacklist
 * Follows the same pattern as SmEventCheck tag-actions component
 */
export const ReviewQueueAction: React.FC<ReviewQueueActionsProps> = ({
    display,
    dataProvider,
    marketId,
    setState,
    reloadPage,
}) => {
    const { selectedIds, data } = useListContext<RaRecord>();
    const notify = useNotify();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [mode, setMode] = useState<string | null>(null); // 'add' or 'ignore'

    if (!display || selectedIds.length === 0) return null;

    const openDialog = (dialogMode: string): void => {
        setMode(dialogMode);
        setDialogOpen(true);
    };

    const handleDialogClose = (): void => {
        setDialogOpen(false);
    };

    /**
     * Core logic for adding to review queue or ignoring
     * Maps the selected rows and calls the appropriate API endpoint
     *
     * @param mode - The operation mode ('add' or 'ignore')
     * @param statusValue - The numeric status value to set. //-1: Ignored 0: Add new 1: Pending 2: Verified 3: Manual review
     */
    const handleSave = (mode: string, statusValue: number): void => {
        setState((prevState: any) => ({ ...prevState, openBackdrop: true })); // Open backdrop in parent component
        handleDialogClose();

        let selectedRows = (data || []).filter(row => selectedIds.includes(row.id));
        let postData = selectedRows.map(row => ({
            eventId: row.id,
            marketId: marketId,
            status: statusValue,
            siteId: row.siteId,
        }));

        dataProvider.postData('QueueUpload', 'UpsertQueueUpload', postData)
            .then((response: any) => {
                if (response?.data && response.data?.success) {
                    notify(response.data.message, { type: 'success' });
                } else {
                    notify(`Error: ${response?.body || response?.message || 'Unknown Error'}`, { type: 'error' });
                }
            })
            .catch((error: Error) => {
                notify(`Error: ${error.message}`, { type: 'error' });
            })
            .finally(() => {
                reloadPage(); // Reload the page
                setState((prevState: any) => ({ ...prevState, openBackdrop: false })); // Close backdrop in parent component
            });
    };

    return (
        <>
            <Button sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#388e3c'
                }
            }}
            variant='contained'
            startIcon={<CheckCircleOutline />}
            onClick={() => openDialog('add')}
            >
                Add to Queue
            </Button>

            <Button sx={{
                backgroundColor: '#f44336',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#d32f2f'
                }
            }}
            variant='contained'
            startIcon={<BlockOutlined />}
            onClick={() => openDialog('ignore')}
            >
                Ignore
            </Button>

            <ConfirmActionDialog
                open={dialogOpen}
                mode={mode || ''}
                selectedCount={selectedIds.length}
                onClose={handleDialogClose}
                onSave={handleSave}
                dataRows={ (data || []).filter(row => selectedIds.includes(row.id))}
            />
        </>
    );
};
